import { ButtonHTMLAttributes, ReactNode, forwardRef } from 'react';
import Spinner from './Spinner';

type Variant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'destructive';
type Tone = 'dark' | 'light';
type Size = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  tone?: Tone;
  size?: Size;
  icon?: ReactNode;
  iconOnly?: boolean;
  loading?: boolean;
  iconPosition?: 'left' | 'right';
}

const variantClasses: Record<Variant, Record<Tone, string>> = {
  primary: {
    dark: 'bg-brand-primary-500 text-brand-neutral border border-brand-primary-400 hover:bg-brand-primary-600 hover:border-brand-primary-500',
    light: 'bg-brand-primary-500 text-white border border-brand-primary-400 hover:bg-brand-primary-600 hover:border-brand-primary-500',
  },
  secondary: {
    dark: 'bg-brand-offwhite text-brand-neutral border border-white hover:bg-gray-200',
    light: 'bg-brand-offwhite text-brand-neutral border border-white hover:bg-gray-200',
  },
  outline: {
    dark: 'bg-transparent text-brand-neutral border border-brand-neutral/20 hover:bg-brand-neutral/5',
    light: 'bg-transparent text-white-alpha-90 border border-white-alpha-40 hover:bg-white/5',
  },
  ghost: {
    dark: 'bg-transparent text-brand-neutral border border-transparent hover:bg-brand-neutral/5',
    light: 'bg-transparent text-white-alpha-90 border border-transparent hover:bg-white/5',
  },
  destructive: {
    dark: 'bg-destructive text-brand-offwhite border border-destructive-light hover:bg-destructive-light',
    light: 'bg-destructive text-brand-offwhite border border-destructive-light hover:bg-destructive-light',
  },
};

const sizeClasses: Record<Size, string> = {
  sm: 'h-8 px-3 text-[13px] gap-1 rounded-md',
  md: 'h-10 px-4 text-base gap-2 rounded-md',
  lg: 'h-12 px-6 text-lg gap-2 rounded-lg',
};

const iconOnlySizeClasses: Record<Size, string> = {
  sm: 'h-8 w-8 rounded-md',
  md: 'h-10 w-10 rounded-md',
  lg: 'h-12 w-12 rounded-lg',
};

const iconSizes: Record<Size, number> = {
  sm: 14,
  md: 16,
  lg: 18,
};

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      tone,
      size = 'md',
      icon,
      iconOnly = false,
      loading = false,
      disabled = false,
      className = '',
      children,
      iconPosition = 'left',
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || loading;
    const resolvedTone = tone ?? (variant === 'primary' ? 'light' : 'dark');

    const classes = [
      'inline-flex items-center justify-center font-bold transition-colors whitespace-nowrap',
      iconOnly ? iconOnlySizeClasses[size] : sizeClasses[size],
      variantClasses[variant][resolvedTone],
      isDisabled ? 'opacity-50 pointer-events-none' : 'cursor-pointer',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <button
        ref={ref}
        className={classes}
        disabled={isDisabled}
        {...props}
      >
        {loading ? (
          <Spinner size={iconSizes[size]} />
        ) : icon && iconPosition === 'left' ? (
          <span className="shrink-0 flex items-center justify-center" style={{ width: iconSizes[size], height: iconSizes[size] }}>
            {icon}
          </span>
        ) : null}
        {!iconOnly && children}
        {icon && iconPosition === 'right' ? (
          <span className="shrink-0 flex items-center justify-center" style={{ width: iconSizes[size], height: iconSizes[size] }}>
            {icon}
          </span>
        ) : null}
      </button>
    );
  }
);

Button.displayName = 'Button';

export default Button;
